import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Network,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import { EmptyState, PageHeader, StatusBadge } from "../components/Ui";
import { api } from "../lib/api";
import { displayBatchMessage, displayError, formatDate, truncate } from "../lib/format";
import {
  buildGoogleMaxBatchRequest,
  googleMaxBatchCounts,
  isGoogleMaxBatchTerminal,
} from "../lib/googleMaxBatch";
import { notify } from "../lib/notify";
import type { GoogleMaxBatch } from "../lib/types";

const ACTIVE_BATCH_STORAGE_KEY = "higgsfield2api.google-max-batch-id";
const DEFAULT_FORM = {
  accounts: "",
  proxy_url: "",
  local_proxy_url: "http://127.0.0.1:1082",
  concurrency: 1,
};

export default function GoogleMaxBatchPage() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [showProxy, setShowProxy] = useState(false);
  const [showLocalProxy, setShowLocalProxy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(() => sessionStorage.getItem(ACTIVE_BATCH_STORAGE_KEY));
  const [batch, setBatch] = useState<GoogleMaxBatch | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const completedNotice = useRef<string | null>(null);

  const inputLineCount = useMemo(
    () => form.accounts.split(/\r?\n/).filter((line) => line.trim()).length,
    [form.accounts],
  );
  const terminal = isGoogleMaxBatchTerminal(batch);

  useEffect(() => {
    if (!batchId) return;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const result = await api.googleMaxBatch(batchId, controller.signal);
        if (stopped) return;
        setBatch(result.batch);
        setPollingError(null);
        if (isGoogleMaxBatchTerminal(result.batch)) {
          sessionStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
          setBatchId(null);
          if (completedNotice.current !== result.batch.id) {
            completedNotice.current = result.batch.id;
            const finalCounts = googleMaxBatchCounts(result.batch);
            notify(`Max 初始化完成：${finalCounts.imported} / ${finalCounts.total} 个已导入`, finalCounts.failed ? "info" : "success");
          }
          return;
        }
      } catch (caught) {
        if (!controller.signal.aborted) setPollingError(displayError(caught));
      }
      if (!stopped) timer = window.setTimeout(() => void poll(), 2_000);
    };

    void poll();
    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [batchId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    let prepared: ReturnType<typeof buildGoogleMaxBatchRequest>;
    try {
      prepared = buildGoogleMaxBatchRequest(form);
    } catch (caught) {
      notify(displayError(caught), "error");
      return;
    }

    setSubmitting(true);
    setPollingError(null);
    try {
      const result = await api.createGoogleMaxBatch(prepared.payload);
      // Credentials and proxy authentication only live in component memory
      // until the backend accepts them. Never persist either value locally.
      setForm((current) => ({ ...current, accounts: "", proxy_url: "", local_proxy_url: "" }));
      setShowProxy(false);
      setShowLocalProxy(false);
      setBatch(result.batch);
      completedNotice.current = null;
      if (!result.batch.id) throw new Error("批次接口未返回批次 ID");
      if (isGoogleMaxBatchTerminal(result.batch)) {
        const finalCounts = googleMaxBatchCounts(result.batch);
        notify(`批次完成：${finalCounts.imported} / ${finalCounts.total} 个已导入`, finalCounts.failed ? "info" : "success");
      } else {
        sessionStorage.setItem(ACTIVE_BATCH_STORAGE_KEY, result.batch.id);
        setBatchId(result.batch.id);
        notify(`已提交 ${prepared.accountCount} 个 Google 账号`, "success");
      }
    } catch (caught) {
      notify(displayError(caught), "error");
    } finally {
      setSubmitting(false);
    }
  }

  function startNewBatch() {
    sessionStorage.removeItem(ACTIVE_BATCH_STORAGE_KEY);
    setBatchId(null);
    setBatch(null);
    setPollingError(null);
    completedNotice.current = null;
  }

  const active = Boolean(batchId) || Boolean(batch && !terminal);

  return (
    <div className="page-stack google-max-page">
      <PageHeader
        title="Google Max 免费额度初始化"
        description="批量登录 Google，初始化 Cinema Studio Pro 免费额度，确认至少 2 条后自动导入远端并仅开启 videos-max。"
        actions={batch && <button type="button" className="button button-secondary" disabled={active} onClick={startNewBatch}><RefreshCw size={15} />新建批次</button>}
      />

      <div className="max-workflow-strip" aria-label="批次执行流程">
        <WorkflowStep icon={Network} step="01" title="代理登录" detail="成功后复用会话" />
        <WorkflowStep icon={Sparkles} step="02" title="额度初始化" detail="达标立即停止" />
        <WorkflowStep icon={ShieldCheck} step="03" title="验证额度" detail="Max ≥ 2 条" />
        <WorkflowStep icon={UploadCloud} step="04" title="远端导入" detail="仅启用 videos-max" />
      </div>

      {!batch ? (
        <section className="panel max-batch-create-panel">
            <div className="panel-heading">
            <div><h3>创建初始化批次</h3><p>已启用内置快速省流策略；凭据和代理认证成功提交后立即从页面内存清除。</p></div>
            <LockKeyhole size={18} />
          </div>
          <form className="form-stack" onSubmit={submit}>
            <label className="field">
              <span>Google 账号凭据</span>
              <textarea
                className="max-accounts-input"
                value={form.accounts}
                onChange={(event) => setForm({ ...form, accounts: event.target.value })}
                placeholder={"user1@example.com|password\nuser2@example.com|password|recovery2@example.com\nuser3@example.com---password---recovery3@example.com"}
                rows={10}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                disabled={submitting}
                required
              />
            </label>
            <div className="max-input-meta"><span>{inputLineCount ? `已粘贴 ${inputLineCount} 行账号，可混用多种格式` : "每行一个账号，支持 email|password、email|password|recovery_email 和 --- 三段格式"}</span><span>最多 500 行</span></div>

            <label className="field">
              <span>账号出口代理 URL</span>
              <div className="secret-input">
                <input
                  type={showProxy ? "text" : "password"}
                  value={form.proxy_url}
                  onChange={(event) => setForm({ ...form, proxy_url: event.target.value })}
                  placeholder="http://user:password@host:port"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={submitting}
                  required
                />
                <button type="button" className="icon-button" onClick={() => setShowProxy((value) => !value)} aria-label={showProxy ? "隐藏代理" : "显示代理"} title={showProxy ? "隐藏代理" : "显示代理"}>
                  {showProxy ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <p className="batch-input-hint">账号代理随本批次用于 Google 登录，并随达标账号的会话导入远端；可为每个新批次更换。</p>

            <label className="field">
              <span>本机中转代理 URL（可选）</span>
              <div className="secret-input">
                <input
                  type={showLocalProxy ? "text" : "password"}
                  value={form.local_proxy_url}
                  onChange={(event) => setForm({ ...form, local_proxy_url: event.target.value })}
                  placeholder="留空则自动检测系统代理"
                  autoComplete="new-password"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={submitting}
                />
                <button type="button" className="icon-button" onClick={() => setShowLocalProxy((value) => !value)} aria-label={showLocalProxy ? "隐藏本机中转代理" : "显示本机中转代理"} title={showLocalProxy ? "隐藏本机中转代理" : "显示本机中转代理"}>
                  {showLocalProxy ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <p className="batch-input-hint">默认使用 <code>http://127.0.0.1:1082</code>；留空由后端自动检测系统代理。填写后，本批次先连接本机中转代理，再连接上方账号出口代理。账号、两级代理及其中的密码均不写入浏览器存储，提交成功后立即清空。</p>

            <label className="field max-concurrency-field"><span>并发登录数</span><input type="number" min={1} max={10} value={form.concurrency} onChange={(event) => setForm({ ...form, concurrency: Number(event.target.value) })} required /></label>

            <div className="callout max-policy-callout"><ShieldCheck size={15} /><span><strong>快速省流模式已内置：</strong>登录成功后复用会话，Max free 额度达到 2 条便立即停止初始化并导入；未达标则创建项目、刷新额度，最多初始化 3 次。仅导入达标账号并开启 <code>videos-max</code>。</span></div>
            <div className="modal-actions"><button type="submit" className="button button-primary" disabled={submitting}>{submitting ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />}{submitting ? "正在安全提交" : "开始批量初始化"}</button></div>
          </form>
        </section>
      ) : (
        <BatchProgress batch={batch} pollingError={pollingError} running={active} />
      )}
    </div>
  );
}

function WorkflowStep({ icon: Icon, step, title, detail }: { icon: typeof Network; step: string; title: string; detail: string }) {
  return <div className="max-workflow-step"><span className="max-workflow-icon"><Icon size={17} /></span><div><small>{step}</small><strong>{title}</strong><span>{detail}</span></div></div>;
}

function BatchProgress({ batch, pollingError, running }: { batch: GoogleMaxBatch; pollingError: string | null; running: boolean }) {
  const counts = googleMaxBatchCounts(batch);
  const progress = counts.total ? Math.min(100, Math.round(((counts.succeeded + counts.failed) / counts.total) * 100)) : 0;
  return (
    <section className="panel max-batch-progress-panel">
      <div className="max-batch-progress-heading">
        <span className={`login-job-icon ${running ? "login-job-icon-running" : counts.failed ? "login-job-icon-danger" : "login-job-icon-success"}`}>
          {running ? <LoaderCircle className="spin" size={20} /> : counts.failed ? <AlertTriangle size={20} /> : <CheckCircle2 size={20} />}
        </span>
        <div><div className="login-job-title"><strong>{running ? "Max 初始化批次执行中" : "Max 初始化批次已完成"}</strong><StatusBadge value={batch.status} /></div><p>批次 <code>{batch.id}</code> · 提交 {formatDate(batch.created_at)}{batch.completed_at ? ` · 完成 ${formatDate(batch.completed_at)}` : ""}</p></div>
      </div>

      <div className="max-progress-track" aria-label={`批次进度 ${progress}%`}><span style={{ width: `${progress}%` }} /></div>
      <div className="max-progress-summary">
        <SummaryCell label="账号总数" value={counts.total} />
        <SummaryCell label="执行中" value={counts.active} />
        <SummaryCell label="额度达标" value={counts.succeeded} />
        <SummaryCell label="远端已导入" value={counts.imported} />
        <SummaryCell label="未完成" value={counts.failed} danger={counts.failed > 0} />
      </div>

      {pollingError && <div className="callout callout-warning"><AlertTriangle size={15} /><span>状态刷新失败，系统将自动重试：{pollingError}</span></div>}
      {batch.error && <div className="form-error">{displayError(batch.error)}</div>}

      <div className="max-results-table table-wrap">
        {batch.items.length ? (
          <table>
            <thead><tr><th>账号引用</th><th>当前状态</th><th className="numeric">Max 免费额度</th><th className="numeric">初始化尝试</th><th className="numeric">网络重试</th><th>结果</th></tr></thead>
            <tbody>{batch.items.map((item, index) => (
              <tr key={`${item.account_ref}-${index}`}>
                <td><code>{item.account_ref}</code></td>
                <td><StatusBadge value={item.status} /></td>
                <td className="numeric"><strong className={Number(item.quota) >= 2 ? "quota-success" : ""}>{item.quota ?? "-"}</strong></td>
                <td className="numeric">{item.initialization_attempts ?? 0} / 3</td>
                <td className="numeric">{item.network_retries ?? 0} / 3</td>
                <td className="max-result-message" title={item.message ? displayBatchMessage(item.message) : undefined}>{item.message ? truncate(displayBatchMessage(item.message), 72) : item.status === "IMPORTED" || item.status === "SUCCEEDED" ? "凭证已导入远端，仅启用 videos-max" : <span className="muted">等待更新</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <EmptyState label="批次已创建，正在等待账号任务" />}
      </div>
    </section>
  );
}

function SummaryCell({ label, value, danger = false }: { label: string; value: number; danger?: boolean }) {
  return <div><span>{label}</span><strong className={danger ? "summary-danger" : ""}>{value}</strong></div>;
}
